import * as ExcelJS from 'exceljs';

import { getInspectionDeviceInfoWithLogs } from '@/apis/report/plan';
import { copyTemplate } from '@/utils/excelExport/excelExportUtils';
import { safeMerge } from '@/utils/safeMerge';

export const exportPerformanceHistory = async (
  workbook: ExcelJS.Workbook,
  inspectionAreaId: number
) => {
  const response = await fetch(
    `/report_sbm/5.2 기계설비 성능점검 대상 설비 성능점검 이력표.xlsx`,
    {
      cache: 'no-store',
    }
  );
  const arrayBuffer = await response.arrayBuffer();
  const newWorkBook = new ExcelJS.Workbook();
  await newWorkBook.xlsx.load(arrayBuffer);

  // 복사 하려는 워크시트
  const copySheet = newWorkBook.getWorksheet(1);
  // 복사 받을 워크시트
  let mainSheet = workbook.addWorksheet(
    'V. 기계설비 성능점검 대상 설비 성능점검 이력표'
  );

  copyTemplate(mainSheet, copySheet, []);

  // 데이터 넣어주는 부분
  const planData = await getInspectionDeviceInfoWithLogs(inspectionAreaId);

  if (mainSheet && planData) {
    const { inspectionDeviceInfo } = planData.data;

    // Step 1: Extract template devices from template
    const templateDevices: Array<{
      rowNumber: number;
      deviceName: string;
      groupTemplate: string;
    }> = [];

    mainSheet.eachRow((row, rowNumber) => {
      if (rowNumber >= 8) {
        const deviceName = row.getCell(2).value as string;
        const groupTemplate = row.getCell(1).value as string;
        if (deviceName && deviceName.trim() !== '') {
          templateDevices.push({
            rowNumber,
            deviceName,
            groupTemplate
          });
        }
      }
    });

    // Step 2: Set year headers
    const fullYear = new Date().getFullYear();
    mainSheet.getRow(6).getCell(10).value = fullYear - 3;
    mainSheet.getRow(6).getCell(11).value = fullYear - 2;
    mainSheet.getRow(6).getCell(12).value = fullYear - 1;
    mainSheet.getRow(6).getCell(13).value = fullYear;

    // Step 3: Group template devices by groupTemplate
    const groupedDevices = new Map<string, typeof templateDevices>();
    
    templateDevices.forEach(device => {
      if (!groupedDevices.has(device.groupTemplate)) {
        groupedDevices.set(device.groupTemplate, []);
      }
      groupedDevices.get(device.groupTemplate)!.push(device);
    });

    // Step 4: Process each group sequentially
    let currentRow = 8;

    for (const [, devices] of Array.from(groupedDevices.entries())) {
      const groupStartRow = currentRow;
      
      // Process each device in group
      for (const templateDevice of devices) {
        const { deviceName } = templateDevice;
        
        // Find matching device in data
        const matchedDeviceName = inspectionDeviceInfo.find(
          (device) => device.templateDeviceName === deviceName
        );

        if (matchedDeviceName) {
          if (
            matchedDeviceName?.deviceInfo?.length &&
            matchedDeviceName?.deviceInfo?.length > 0
          ) {
            const rowsToInsert = matchedDeviceName.deviceInfo.length - 1;
            
            // Insert rows if needed
            if (rowsToInsert > 0) {
              mainSheet.spliceRows(
                currentRow + 1,
                0,
                ...Array(rowsToInsert).fill([])
              );
            }

            // Process each device info
            matchedDeviceName.deviceInfo.forEach((device, index) => {
              const row = mainSheet.getRow(currentRow);
              row.getCell(3).value = device.device_num;
              row.getCell(6).value = device.location;
              
              device.log.forEach((log, logIndex) => {
                let result = '';
                if (log.updatedAt) {
                  const d = new Date(log.updatedAt);
                  const day = String(d.getDate()).padStart(2, '0');
                  const month = String(d.getMonth() + 1).padStart(2, '0');
                  result = day + '.' + month;
                }
                row.getCell(10 + logIndex).value = result;
              });

              // Apply formatting
              row.eachCell((cell) => {
                cell.border = {
                  top: { style: 'thin' },
                  left: { style: 'thin' },
                  bottom: { style: 'thin' },
                  right: { style: 'thin' },
                };

                cell.alignment = {
                  vertical: 'middle',
                  horizontal: 'center',
                };

                cell.font = {
                  size: 10,
                };
              });

              currentRow++;
            });

            // Merge cells if rows were inserted
            if (rowsToInsert > 0) {
              const startRow = currentRow - matchedDeviceName.deviceInfo.length;
              const endRow = currentRow - 1;

              // Unmerge any existing A-B merges
              for (let r = startRow; r <= endRow; r++) {
                try {
                  mainSheet.unMergeCells(`A${r}:B${r}`);
                } catch (_) {
                  // ignore if not merged
                }
              }

              // Check for B cell merge conflicts
              let bHasConflict = false;
              for (let r = startRow; r <= endRow; r++) {
                if (mainSheet.getCell(r, 2).isMerged) {
                  bHasConflict = true;
                  break;
                }
              }

              if (!bHasConflict) {
                safeMerge(mainSheet, startRow, 2, endRow, 2);
              }

              // Merge other columns vertically
              safeMerge(mainSheet, startRow, 1, endRow, 1);
              safeMerge(mainSheet, startRow, 7, endRow, 7);
              safeMerge(mainSheet, startRow, 8, endRow, 8);
              safeMerge(mainSheet, startRow, 9, endRow, 9);
            }

            // Set total amount, inspection amount, and ratio
            const firstRow = currentRow - matchedDeviceName.deviceInfo.length;
            mainSheet.getRow(firstRow).getCell(7).value = matchedDeviceName.totalAmount;
            mainSheet.getRow(firstRow).getCell(9).value = matchedDeviceName.inspectionAmount;

            const ratio = matchedDeviceName.inspectionAmount / matchedDeviceName.totalAmount;
            const rounded = Math.round(ratio * 100) / 100;
            mainSheet.getRow(firstRow).getCell(8).value = rounded;
            mainSheet.getRow(firstRow).getCell(8).numFmt = '0%';

          } else {
            // No device info available
            mainSheet.getRow(currentRow).getCell(3).value = '-';
            mainSheet.getRow(currentRow).getCell(6).value = '-';
            mainSheet.getRow(currentRow).getCell(7).value = 0;
            mainSheet.getRow(currentRow).getCell(8).value = '-';
            mainSheet.getRow(currentRow).getCell(9).value = 0;
            currentRow++;
          }
        } else {
          // No matching device found
          mainSheet.getRow(currentRow).getCell(3).value = '-';
          mainSheet.getRow(currentRow).getCell(6).value = '-';
          mainSheet.getRow(currentRow).getCell(7).value = 0;
          mainSheet.getRow(currentRow).getCell(8).value = '-';
          mainSheet.getRow(currentRow).getCell(9).value = 0;
          currentRow++;
        }
      }

      // Merge column A for entire group
      if (currentRow > groupStartRow) {
        // Unmerge first to ensure clean merge
        try {
          mainSheet.unMergeCells(`A${groupStartRow}:A${currentRow - 1}`);
        } catch (_) {
          // ignore if not merged
        }
        
        // Keep value from first row and clear others
        for (let r = groupStartRow + 1; r <= currentRow - 1; r++) {
          mainSheet.getRow(r).getCell(1).value = '';
        }
        
        // Merge column A vertically for entire group
        safeMerge(mainSheet, groupStartRow, 1, currentRow - 1, 1);
      }
    }

    // Step 5: Handle special templates - clear column B and merge A-B
    const specialTemplates = [
      '오·배수 통기 및 우수배수설비',
      '위생기구설비',
      '배관설비', 
      '덕트설비',
      '보온설비',
      '자동제어설비',
      '방음·방진·내진설비'
    ];

    mainSheet.eachRow((row, rowNumber) => {
      if (rowNumber >= 8) {
        const deviceName = row.getCell(2).value as string;
        const groupTemplate = row.getCell(1).value as string;
        
        // Check if is special template
        if (specialTemplates.includes(deviceName) || specialTemplates.includes(groupTemplate)) {
          // Clear column B content
          row.getCell(2).value = '';
          
          try {
            mainSheet.unMergeCells(`A${rowNumber}:B${rowNumber}`);
          } catch (_) {
            // ignore if not merged
          }
          safeMerge(mainSheet, rowNumber, 1, rowNumber, 2);
        }
      }
    });

    // Hide columns D and E
    mainSheet.getColumn(4).hidden = true;
    mainSheet.getColumn(5).hidden = true;
    
    // Apply thick borders to outer edges
    const lastRow = currentRow - 1;
    for (let row = 5; row <= lastRow; row++) {
      for (let col = 1; col <= 14; col++) {
        const cell = mainSheet.getCell(row, col);
        const isTopRow = row === 5;
        const isBottomRow = row === lastRow;
        const isLeftCol = col === 1;
        const isRightCol = col === 14;
        
        if (isTopRow || isBottomRow || isLeftCol || isRightCol) {
          cell.border = {
            ...cell.border,
            top: isTopRow ? { style: 'thick' } : cell.border?.top,
            bottom: isBottomRow ? { style: 'thick' } : cell.border?.bottom,
            left: isLeftCol ? { style: 'thick' } : cell.border?.left,
            right: isRightCol ? { style: 'thick' } : cell.border?.right,
          };
        }
      }
    }

    // Ensure inner grid borders exist (especially for column N cells that were not touched earlier)
    for (let row = 5; row <= lastRow; row++) {
      for (let col = 1; col <= 14; col++) {
        const cell = mainSheet.getCell(row, col);
        const edgeTop = cell.border?.top?.style === 'thick';
        const edgeBottom = cell.border?.bottom?.style === 'thick';
        const edgeLeft = cell.border?.left?.style === 'thick';
        const edgeRight = cell.border?.right?.style === 'thick';

        cell.border = {
          top: edgeTop ? cell.border?.top : (cell.border?.top || { style: 'thin' }),
          bottom: edgeBottom ? cell.border?.bottom : (cell.border?.bottom || { style: 'thin' }),
          left: edgeLeft ? cell.border?.left : (cell.border?.left || { style: 'thin' }),
          right: edgeRight ? cell.border?.right : (cell.border?.right || { style: 'thin' }),
        };
      }
    }

    mainSheet.pageSetup = {
      paperSize: 9,
      fitToPage: true,
      scale: 100,
      fitToWidth: 1,
      fitToHeight: 0,
    };
    mainSheet.views = [{ style: 'pageBreakPreview' }];
  }
};
